import { useCurrentUser } from '@/features/auth/state/auth'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import api, { publicApi } from '@/lib/api'
import { TypeContrato } from '@/features/agreement/utils/enum'

export function useFetchAgreementByRead(id_tenant: string, contracts: TypeContrato[]) {
  return useQuery({
    queryKey: ['agreement', id_tenant, contracts],
    queryFn: async () => {
      const response = await api.get(
        `agreement/read-for-client?id_tenant=${id_tenant}&contracts=${contracts.join(',')}`
      )

      if (response.data) {
        return response.data.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: !!id_tenant && contracts.length > 0
  })
}

export function useFetchAgreementByIdUser(id_usuario: string) {
  return useQuery({
    queryKey: ['agreement'],
    queryFn: async () => {
      const response = await api.get(`agreement/ativo/user/${id_usuario}`)

      if (response.data) {
        return response.data.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: !!id_usuario
  })
}

export function useUpdateAgreementUser() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Usuário ou Tenant não autenticado')

      const response = await api.post(`/agreement/update/user`)

      if (!response.data) {
        throw new Error('Erro ao atualizar contrato')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement'] })
    }
  })
}

export function useFetchAgreementById(id_client: string) {
  return useQuery({
    queryKey: ['agreement'],
    queryFn: async () => {
      const response = await publicApi.get(`agreement/ativo/${id_client}`)

      if (response.data) {
        return response.data.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: !!id_client
  })
}

export function useCreateAgreement(id_client: string) {
  const queryClient = useQueryClient()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async ({ contracts }: { contracts: TypeContrato[] }) => {
      if (!user) throw new Error('Usuário ou Tenant não autenticado')

      const body = {
        contracts,

        id_client: id_client
      }

      if (!body.contracts || !body.id_client) {
        throw new Error('Campos obrigatórios ausentes')
      }

      const response = await api.post(`/agreement/vincular`, body)

      if (!response.data) {
        throw new Error('Erro ao criar contrato')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement'] })
    }
  })
}
interface UpdateProductPayload {
  contracts: TypeContrato[]
  typeRegime: string
}

export function useAddAgreementAndProduct(id_client: string) {
  const queryClient = useQueryClient()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async ({ contracts, typeRegime }: UpdateProductPayload) => {
      if (!user) throw new Error('Usuário não autenticado')

      if (!contracts.length || !id_client || !typeRegime) {
        throw new Error('Campos obrigatórios ausentes')
      }

      const body = {
        contracts,
        id_user: user.id,
        id_client,
        typeRegime
      }

      const response = await api.post('/agreement/new', body)

      if (!response.data) {
        throw new Error('Erro ao atualizar contrato')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['agreement'] })
      queryClient.invalidateQueries({ queryKey: ['product', id_client] })
    }
  })
}
export function useUpdateAgreementProduct(id_client: string) {
  const queryClient = useQueryClient()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async ({ contracts, typeRegime }: UpdateProductPayload) => {
      if (!user) throw new Error('Usuário não autenticado')

      if (!contracts.length || !id_client || !typeRegime) {
        throw new Error('Campos obrigatórios ausentes')
      }

      const body = {
        contracts,
        id_user: user.id,
        id_client,
        typeRegime
      }

      const response = await api.post('/agreement/update/product', body)

      if (!response.data) {
        throw new Error('Erro ao atualizar contrato')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['agreement'] })
      queryClient.invalidateQueries({ queryKey: ['product', id_client] })
    }
  })
}

export function useCreateAgreementRede(id_net: string) {
  const queryClient = useQueryClient()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async ({ contracts }: { contracts: TypeContrato[] }) => {
      if (!user) throw new Error('Usuário ou Tenant não autenticado')

      const body = {
        contracts,
        id_net: id_net
      }

      if (!body.contracts || !body.id_net) {
        throw new Error('Campos obrigatórios ausentes')
      }

      const response = await api.post(`/agreement/vincular/net`, body)

      if (!response.data) {
        throw new Error('Erro ao criar contrato')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement'] })
    }
  })
}

export function useUpdateAgreement(id_client?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const body = {
        id_client
      }

      if (!body.id_client) {
        throw new Error('Campos obrigatórios ausentes')
      }

      const response = await api.post(`/agreement/update`, body)

      if (!response.data) {
        throw new Error('Erro ao atualizar contrato')
      }

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement'] })
    }
  })
}

// Fetch all active agreements using useQuery
export function useFetchAllActiveAgreements() {
  return useQuery({
    queryKey: ['agreement', 'active'],
    queryFn: async () => {
      const response = await api.get('/agreement/ativos')
      if (response.data && response.data.data) {
        return response.data.data
      } else {
        throw new Error('Data não encontrado no resultado')
      }
    }
  })
}

// Add images to an agreement using useMutation
export function useAddAgreementImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, imagesData }: { id: number; imagesData: any }) => {
      const response = await api.patch(`/agreements/${id}/images`, imagesData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      if (response.data && response.data.data) {
        return response.data.data
      } else {
        throw new Error('Data não encontrado no resultado')
      }
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ['agreement', id] })
    }
  })
}

// Deactivate an agreement by id using useMutation
export function useDeactivateAgreement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, idUsuario }: { id: number; idUsuario: string }) => {
      const response = await api.post(
        `/agreements/desativar/${id}`,
        { idUsuario },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.data) {
        return response.data.data
      } else {
        throw new Error('Data não encontrado no resultado')
      }
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ['agreement', id] })
      queryClient.invalidateQueries({ queryKey: ['agreements', 'active'] })
    }
  })
}
